import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ChartOfAccountsTemplateUploadSource } from "@common/constants/domains/accounting/chartOfAccountsTemplateAccount";
import { chartOfAccountsTemplateContract } from "@common/contracts/chart-of-accounts-template.contract";
import { ChartOfAccountsTemplateUploadResponseSchema } from "@common/schemas/chart-of-accounts-template-upload.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { backendClient, SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useScope } from "@state/scopeContext.lib";
import { extractApiErrorMessage } from "./apiError";

interface ParseUploadParams {
    file: File;
    source: ChartOfAccountsTemplateUploadSource;
}

interface ParseQuickbooksParams {
    authCode: string;
    realmId: string;
}

const chartOfAccountsTemplateClient = getBackendAPIClient(
    chartOfAccountsTemplateContract,
);

const FALLBACK_ERROR_MESSAGE = "Couldn't read this file. Try again.";

interface UploadErrorMessageParams {
    error: Error;
    fallback?: string;
}

export function chartOfAccountsTemplateUploadErrorMessage({
    error,
    fallback = FALLBACK_ERROR_MESSAGE,
}: UploadErrorMessageParams): string {
    return isAxiosError(error)
        ? extractApiErrorMessage(error.response?.data, fallback)
        : fallback;
}

export function useParseChartOfAccountsTemplateUploadMutation() {
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async ({ file, source }: ParseUploadParams) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const formData = new FormData();
            formData.append("source", source);
            formData.append("file", file);

            const response = await backendClient.post(
                `/chart-of-accounts-templates/organizations/${organizationId}/parse-upload`,
                formData,
                { headers: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" } },
            );
            return ChartOfAccountsTemplateUploadResponseSchema.parse(
                response.data,
            );
        },
    });
}

export function useParseChartOfAccountsTemplateFromQuickbooksMutation() {
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async ({ authCode, realmId }: ParseQuickbooksParams) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }
            const response =
                await chartOfAccountsTemplateClient.parseChartOfAccountsTemplateFromQuickbooks(
                    {
                        params: { organizationId },
                        body: { authCode, realmId },
                        extraHeaders: {
                            [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                        },
                    },
                );
            return ChartOfAccountsTemplateUploadResponseSchema.parse(
                response.body,
            );
        },
    });
}
